import { VRBoard } from './ver-assets/board.js';
import { VRPerson } from './ver-assets/person.js';
import { VROverlay } from './ver-assets/overlays.js';
import { VRSign } from './ver-assets/sign.js';
import { VRTable } from './ver-assets/table.js';
import {
  VRHotspot,
  VRIconHotspot,
  HotspotNode
} from './ver-assets/hotspots.js';
import { Node } from '../../third-party/webxr/render/core/node.js';
import { BoxBuilder } from '../../third-party/webxr/render/geometry/box-builder.js';
import { PbrMaterial } from '../../third-party/webxr/render/materials/pbr.js';
import {
  mat4,
  vec3,
  quat
} from '../../third-party/webxr/render/math/gl-matrix.js';
import { VRCustomOverlay } from './ver-assets/custom-overlay.js';

export class VRScene {
  constructor (vr, options) {
    this.vr = vr;
    this.options = options;
    this.loading = false;
    this.togglingDayNight = false;
    if (!this.options.yaw) this.options.yaw = 0;
    if (!this.options.pitch) this.options.pitch = 0;
    if (!this.options.fov) this.options.fov = 90;

    this.boxes = [];

    this.hasWelcome = false;
    this.overlays = [];
    this.hotspots = [];
    this.container = $('<div class="ca-vr_overlay-container"></div>');
    this.container.hide();
    vr.container.append(this.container);

    // Hotspots
    this.addHotspots(this.options.hotspots);
    this.addAssets(this.options.overlays);

    // lookat alignment
    this.lookAtPositionYaw = 0;
    this.lookAtPositionPitch = 0;

    // GL Context
    this.gl = null;
  }

  getContainer () {
    return this.container;
  }

  parseMediaUrl (mediaUrl) {
    return this.vr.parseMediaUrl(mediaUrl).replace('{scene}', this.options.id);
  }

  onLoaded () {
    if (this.snapshotCanvas) {
      this.snapshotCanvas.addClass('ca-vr_snapshotcanvas--removing');
      setTimeout(
        function () {
          this.vr.enableMove(true);
          this.vr.showControls(true);
          this.snapshotCanvas.remove();
        }.bind(this),
        1000
      );
    } else {
      this.vr.enableMove(true);
      this.vr.showControls(true);
    }

    if (this.togglingDayNight) {
      this.vr.lookAt(this.lastYaw, this.lastPitch, this.lastFov, 0);
    } else if (this.hasWelcome) {
      this.vr.lookAt(this.options.yaw, this.options.pitch, 90, 0);
    } else {
      this.vr.lookAt(this.options.yaw, this.options.pitch, 30, 0);
      this.vr.lookAt(
        this.options.yaw,
        this.options.pitch,
        this.vr.getStartFov(),
        1000,
        true
      );

      setTimeout(
        function () {
          this.vr.startAutoRotate();
        }.bind(this),
        1000
      );
    }
    this.container.show();
    this.vr.hideLoading();

    this.loading = false;
    this.togglingDayNight = false;

    this.hotspots.forEach((hotspot) => {
      if(this.vr.editor !== undefined) {
        hotspot.toggleTooltipVisibility(true);
      } else {
        hotspot.toggleTooltipVisibility(false);
      }
    });
  }

  render (t) {
    if (this.loading) return;

    this.hotspots.forEach((hotspot) => {
      hotspot.render(t);
    });
    this.overlays.forEach((overlay) => {
      overlay.render(t);
    });
  }

  // fetches and loads the next scenes image
  preloadImage (callback) {
    const preloadImg = $('<img class="ca-vr_preloadimage" />');
    preloadImg[0].crossOrigin = 'anonymous';
    preloadImg.on(
      'load',
      function () {
        callback.call(this);
      }.bind(this)
    );

    preloadImg[0].src = this.parseMediaUrl(this.options.media);
  }

  canvasSnapshot (callback, zoomEffect) {
    const prevCanvas = this.vr.getCanvas();
    if (prevCanvas) {
      this.snapshotCanvas = $('<canvas class="ca-vr_snapshotcanvas"></canvas>');

      this.snapshotCanvas[0].width = prevCanvas[0].width;
      this.snapshotCanvas[0].height = prevCanvas[0].height;

      this.vr.getContainer().append(this.snapshotCanvas);

      const context = this.snapshotCanvas[0].getContext('2d');

      // we need to redraw the previous canvas

      // the reason we set the below is that, the canvas being created is too big for the clients viewport window
      // so we'll try and restrain it down into the viewport dimensions
      // its still a little off, so there's some offset values I played around with
      context.drawImage(
        prevCanvas[0],
        -200,
        0,
        window.innerWidth + 200,
        window.innerHeight
      );

      // var dataURL = this.snapshotCanvas[0].toDataURL("image/jpeg");
      // var newTab = window.open(this.snapshotCanvas[0].toDataURL('image/jpeg'));
      // newTab.document.write("<img src='" + dataURL + "' alt='from canvas'/>");

      if (!zoomEffect || zoomEffect !== false) {
        this.snapshotCanvas.addClass('ca-vr_snapshotcanvas--zooming');
      }

      callback.call(this);

      /* prevCanvas[0].toBlob(function(blob)
            {
                this.loadImg = $('<img class="ca-vr_loadimage" />');

                let url = URL.createObjectURL(blob);
                this.loadImg.on('load', function()
                {
                    this.vr.getContainer().append(this.loadImg);
                    callback.call(this);
                    URL.revokeObjectURL(url);
                }.bind(this));

                this.loadImg[0].src = url;
            }.bind(this)); */
    } else callback.call(this);
  }

  addHotspots (hotspots) {
    if (hotspots == null) return;

    hotspots.forEach(
      function (h) {
        h.editor = this.vr.options.editor;
        this.addHotspot(h);
      }.bind(this)
    );
  }

  addAssets (assets) {
    if (assets == null) return;

    assets.forEach(
      function (o) {
        o.editor = this.vr.options.editor;
        if (o.type === 'prop_person') {
          this.addPerson(o);
          if (o.welcome) this.hasWelcome = true;
        } else if (o.type === 'prop_board') {
          this.addBoard(o);
        } else if (o.type === 'sign') {
          this.addSign(o);
        } else if (o.type === 'table') {
          this.addTable(o);
        } else if (o.type === 'custom-overlay') {
          this.addCustomOverlay(o);
        }
      }.bind(this)
    );
  }

  // can we pass in the current hotspot clicked?
  // look here for the transition stuff
  loadImage (mediaUrl, yaw, pitch, zoomEffect) {
    this.lastYaw = this.vr.getYaw();
    this.lastPitch = this.vr.getPitch();
    this.lastFov = this.vr.getFov();

    this.loading = true;
    // this.vr.enableMove(false);

    // this.vr.stopAutoRotate();
    // this.vr.showLoading('Loading '+(this.options.name ? this.options.name : 'room')+'...');

    this.preloadImage(function () {
      /* let delay = this.vr.isVrMode() ? 0 : 800;
            if(yaw != null && pitch != null)
            {
                console.log('zoom in');
                this.vr.lookAt(yaw, pitch, this.vr.getMaxFov(), delay);
            } */

      setTimeout(
        function () {
          this.canvasSnapshot(function () {
            setTimeout(
              function () {
                this.loadImageNow(mediaUrl);

                this.overlays.forEach(function (o) {
                  o.onToggleDayNight();
                });
              }.bind(this),
              800
            );
          }, zoomEffect);
        }.bind(this),
        400
      );
    });
  }

  loadImageXR (mediaUrl) {
    this.lastYaw = this.vr.getYaw();
    this.lastPitch = this.vr.getPitch();
    this.lastFov = this.vr.getFov();

    this.loading = true;
    this.preloadImage(function () {
      this.loadImageNowXR(mediaUrl);
    });
  }

  showWelcome (welcomePopup) {
    this.overlays.forEach((o) => {
      if (o.options.welcome) {
        o.div.addClass('ca-vr_overlay_prop_person--welcoming');
        this.vr.lookAt(-o.options.yaw + 8, -9, this.vr.getMaxFov(), 0);

        welcomePopup.open();
        welcomePopup.position(o);
      }
    });
    welcomePopup.open();
    welcomePopup.el.css('left', '35%');
    welcomePopup.el.css('top', 100);
  }

  hideWelcome () {
    this.overlays.forEach((o) => {
      if (o.options.welcome) { o.div.removeClass('ca-vr_overlay_prop_person--welcoming'); }
    });

    this.vr.lookAt(
      this.options.yaw,
      this.options.pitch,
      this.vr.getStartFov(),
      1000,
      true
    );

    this.toggleAllHotspotTooltipsInScene();
  }

  loadImageNow (mediaUrl) {
    this.vr.showLoading(
      'Loading ' + (this.options.name ? this.options.name : 'room') + '...'
    );
    // this.container.hide();
    this.vr.panoViewer.setImage(
      this.parseMediaUrl(mediaUrl || this.options.media)
    );
  }

  loadImageNowXR (mediaUrl) {
    this.vr.panoViewer._image = this.parseMediaUrl(
      mediaUrl || this.options.media
    );
    this.vr.panoViewer._initRenderer();
  }

  toggleDayNight () {
    this.togglingDayNight = true;
    this.loadImage(this.options.media, null, null, false);
  }

  sortOverlays () {
    this.overlays = this.overlays.sort(function (a, b) {
      return a.getDistance() - b.getDistance();
    });

    this.overlays.forEach(function (o, index) {
      o.div.css('z-index', index);
    });
  }

  addHotspot (options, isNewHotspot) {
    let hotspot = null;

    // if(options.type == 'icon') hotspot = new VRSceneJumpHotspot(this.vr, this, options);
    // else
    // used to be new VRIconHotspot(this.vr, this, options)
    if (isNewHotspot) {
      // console.log('new hotspot with no cached options');
      hotspot = new VRIconHotspot(this.vr, this);
    } else {
      // console.log('hotspot with cached options');
      hotspot = new VRIconHotspot(this.vr, this, options);
    }

    if (this.vr.hasEditor()) this.vr.getEditor().addObject(hotspot);

    this.hotspots.push(hotspot);
    this.container.append(hotspot.div);

    hotspot.div.addClass('ca-vr_asset--removed');
    setTimeout(function () {
      hotspot.div.removeClass('ca-vr_asset--removed');
    }, 200);

    this.vr.update();
    return hotspot;
  }

  addPerson (options) {
    const person = new VRPerson(this.vr, this, options);
    this.addOverlay(person);
    return person;
  }

  addBoard (options) {
    const board = new VRBoard(this.vr, this, options);
    this.addOverlay(board);
    return board;
  }

  addCustomOverlay (options) {
    const customOverlay = new VRCustomOverlay(this.vr, this, options);
    this.addOverlay(customOverlay);
    return customOverlay;
  }

  addSign (options) {
    const sign = new VRSign(this.vr, this, options);
    this.addOverlay(sign);
    return sign;
  }

  addTable (options) {
    const table = new VRTable(this.vr, this, options);
    this.addOverlay(table);
    return table;
  }

  addOverlay (overlay) {
    if (this.vr.hasEditor()) {
      this.vr.getEditor().addObject(overlay);
    }

    this.overlays.push(overlay);
    this.container.append(overlay.div);

    if (this.vr.insetMap != null) {
      this.vr.insetMap.addAsset(overlay.options);
    }

    overlay.div.addClass('ca-vr_asset--removed');
    setTimeout(function () {
      overlay.div.removeClass('ca-vr_asset--removed');
    }, 200);

    this.vr.update();
  }

  layout () {
    this.overlays.forEach((o) => o.layout());
  }

  removeAssets () {
    this.overlays.forEach(
      function (a) {
        a.div.remove();
        if (this.vr.insetMap != null) this.vr.insetMap.removeAsset(a.options);
        a = null;
      }.bind(this)
    );

    this.hotspots.forEach(function (h) {
      h.div.remove();
      h = null;
    });

    this.overlays = [];
    this.hotspots = [];
  }

  removeAsset (asset) {
    if (asset instanceof VROverlay) { this.overlays.splice(this.overlays.indexOf(asset), 1); } else if (asset instanceof VRHotspot) { this.hotspots.splice(this.hotspots.indexOf(asset), 1); }

    if (this.vr.insetMap != null) this.vr.insetMap.removeAsset(asset.options);

    asset.div.addClass('ca-vr_asset--removed');
    setTimeout(function () {
      asset.div.remove();
      asset = null;
    }, 500);
  }

  getJsonConfig () {
    this.options.overlays = [];
    this.overlays.forEach(
      function (o, index) {
        this.options.overlays[index] = o.getJsonConfig();
      }.bind(this)
    );

    this.options.hotspots = [];
    this.hotspots.forEach(
      function (h, index) {
        this.options.hotspots[index] = h.getJsonConfig();
      }.bind(this)
    );

    return this.options;
  }

  onStartMoving () {
    this.hotspots.forEach(
      function (h) {
        h.enable(false);
      }
    );

    this.overlays.forEach(
      function (o) {
        o.enable(false);
      }
    );
  }

  onStopMoving () {
    this.hotspots.forEach(
      function (h) {
        h.enable(true);
      }
    );

    this.overlays.forEach(
      function (o) {
        o.enable(true);
      }
    );
  }

  initXR (renderer, xrScene) {
    if (this.xrScene) return;

    this.xrScene = xrScene;

    // this.debugAddCube(renderer, xrScene);
    this.drawHotspotsXR(renderer, xrScene);
  }

  debugAddCube (renderer, xrScene) {
    const boxBuilder = new BoxBuilder();
    boxBuilder.pushCube([0, 0, 0], 0.4);
    const boxPrimitive = boxBuilder.finishPrimitive(renderer);

    const boxMaterial = new PbrMaterial();
    boxMaterial.baseColorFactor.value = [1, 1, 1, 1.0];
    const boxRenderPrimitive = renderer.createRenderPrimitive(
      boxPrimitive,
      boxMaterial
    );

    this.boxNode = new Node();
    this.boxNode.addRenderPrimitive(boxRenderPrimitive);
    this.boxNode.translation = [0, 0, -1];
    this.boxNode.scale = [0.5, 0.5, 0.5];
    xrScene.addNode(this.boxNode);
    this.boxNode.selectable = true;
  }

  drawHotspotsXR (renderer, xrScene) {
    if (this.options.hotspots) {
      // get pitch and yaw
      const vrRef = this.vr;
      this.options.hotspots.forEach(function (h) {
        const hotspot = new HotspotNode(function () {
          // set pitch and yaw to 0 for now
          vrRef.loadSceneXR(h.action.scene);
        });
        let origin = vec3.create();
        origin = [0, 0, 0];

        let upVector = vec3.create();
        upVector = [0, 1, 0];

        // TODO: create a radius to field to be customized as a config input
        // lets convert our pitch and yaw into xyz coords
        // however they MUST be converted to radians as Math.cos/sin expect radian based values
        const radius = 1.5; // distance
        const yawRads = h.yaw * (Math.PI / 180); // rename to yawInRads
        const pitchRads = h.pitch * (Math.PI / 180);

        const x = Math.sin(yawRads) * Math.cos(pitchRads) * radius;
        const y = Math.sin(pitchRads);
        const z = Math.cos(yawRads) * Math.cos(pitchRads) * radius;

        hotspot.translation = [x, y, -z];
        hotspot.scale = [1, 1, 1];

        // create a targetTo matrix so we can use it to get the rotation
        const targetMatrix = mat4.create();
        mat4.targetTo(targetMatrix, hotspot.translation, origin, upVector);

        // get the rotation from the targetTo matrix
        let rotationQuat = quat.create();
        mat4.getRotation(rotationQuat, targetMatrix);
        rotationQuat[0] = rotationQuat[1];
        rotationQuat[1] = 0;
        rotationQuat[2] = rotationQuat[3];
        rotationQuat[3] = 0;

        // convert quaternion to euler
        const ftx = 2.0 * rotationQuat[1];
        const fty = 2.0 * rotationQuat[2];
        const ftz = 2.0 * rotationQuat[3];
        const ftwy = fty * rotationQuat[0];
        const ftxx = ftx * rotationQuat[1];
        const ftxz = ftz * rotationQuat[1];
        const ftyy = fty * rotationQuat[2];

        let degRad = Math.atan2(ftxz + ftwy, 1.0 - (ftxx + ftyy));

        quat.copy(hotspot.rotation, rotationQuat);
        quat.setAxisAngle(hotspot.rotation, vec3.fromValues(0, 1, 0), -degRad);
        rotationQuat = null;
        degRad = null;

        // add this hotspot to the scene
        xrScene.addNode(hotspot);
      });
    }
  }

  saveLayoutTemplate () {
    const assets = [];
    this.overlays.forEach(function (a) {
      assets.push(a.options);
    });
    return JSON.stringify({ assets: assets });
  }

  loadLayoutTemplate (assets) {
    this.removeAssets();
    this.addAssets(assets);
  }

  toggleAllHotspotTooltipsInScene () {
    this.hotspots.forEach((hotspot) => {
      hotspot.toggleTooltipVisibility(this.vr.isEditing());
    });
  }
}
